import { Database, HardDrive } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface Agent {
  id: string;
  name: string;
  type: string;
  status: string;
}

interface SourceSelectionProps {
  data: {
    type: 'POSTGRESQL' | 'FILES';
    agent_id: string;
  };
  onChange: (data: unknown) => void;
  onNext?: () => void;
  onBack?: () => void;
}

export function SourceSelection({ data, onChange }: SourceSelectionProps) {
  const { t } = useTranslation('backups');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<{ type?: string; agent_id?: string }>({});

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/v1/agents', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAgents(Array.isArray(data) ? data : []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const hasAgents = agents.length > 0;
  const selectedAgentIsOnline = data.agent_id
    ? agents.find((a) => a.id === data.agent_id)?.status?.toUpperCase() === 'ONLINE'
    : false;

  return (
    <div className="space-y-6">
      <div>
        <p
          id="backup-config-type-label"
          className="block text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3 ml-1"
        >
          {t('wizard.source.backupType')}
        </p>
        <div
          role="group"
          aria-labelledby="backup-config-type-label"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4"
        >
          <button
            type="button"
            aria-pressed={data.type === 'POSTGRESQL'}
            onClick={() => {
              onChange({ ...data, type: 'POSTGRESQL' });
              setErrors({ ...errors, type: undefined });
            }}
            className={`p-4 sm:p-6 rounded-xl border-2 transition-all text-left ${
              data.type === 'POSTGRESQL'
                ? 'border-brand-600 bg-accent-soft-light dark:bg-accent-soft-dark shadow-brand-200'
                : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
            }`}
          >
            <div className="mb-2 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
              <Database
                className={`h-6 w-6 shrink-0 sm:h-7 sm:w-7 ${
                  data.type === 'POSTGRESQL'
                    ? 'text-brand-600 dark:text-brand-400'
                    : 'text-text-subtle-light dark:text-text-subtle-dark'
                }`}
                strokeWidth={2.5}
              />
              <span className="text-base font-black leading-tight text-slate-900 dark:text-white sm:text-lg">
                PostgreSQL
              </span>
            </div>
            <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark">
              {t('wizard.source.postgresqlDesc')}
            </p>
          </button>
          <button
            type="button"
            aria-pressed={data.type === 'FILES'}
            onClick={() => {
              onChange({ ...data, type: 'FILES' });
              setErrors({ ...errors, type: undefined });
            }}
            className={`p-4 sm:p-6 rounded-xl border-2 transition-all text-left ${
              data.type === 'FILES'
                ? 'border-brand-600 bg-accent-soft-light dark:bg-accent-soft-dark shadow-brand-200'
                : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
            }`}
          >
            <div className="mb-2 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
              <HardDrive
                className={`h-6 w-6 shrink-0 sm:h-7 sm:w-7 ${
                  data.type === 'FILES'
                    ? 'text-brand-600 dark:text-brand-400'
                    : 'text-text-subtle-light dark:text-text-subtle-dark'
                }`}
                strokeWidth={2.5}
              />
              <span className="text-base font-black leading-tight text-slate-900 dark:text-white sm:text-lg">
                {t('wizard.source.filesystem')}
              </span>
            </div>
            <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark">
              {t('wizard.source.filesystemDesc')}
            </p>
          </button>
        </div>
        {errors.type && (
          <p className="mt-2 text-xs font-bold text-state-danger-light dark:text-state-danger-dark ml-1">
            {errors.type}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="backup-config-agent"
          className="block text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3 ml-1"
        >
          {t('wizard.source.selectAgent')}
        </label>
        {loading ? (
          <div className="flex items-center justify-center p-8 border border-surface-border-light dark:border-surface-border-dark rounded-xl">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
          </div>
        ) : !hasAgents ? (
          <div className="p-6 border-2 border-state-warn-light/20 dark:border-state-warn-dark/20 bg-state-warn-bg-light dark:bg-state-warn-bg-dark rounded-xl text-center">
            <div className="w-12 h-12 bg-state-warn-bg-light dark:bg-state-warn-bg-dark rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-state-warn-light dark:text-state-warn-dark"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">
              {t('wizard.source.noAgentsTitle')}
            </h3>
            <p className="text-sm font-medium text-slate-600 dark:text-text-subtle-dark mb-1">
              {t('wizard.source.noAgentsDesc')}
            </p>
            <p className="text-xs text-text-muted-light dark:text-text-muted-dark mb-4">
              {t('wizard.source.installAgent')}
            </p>
            <Link
              to="/agents"
              className="inline-flex min-h-11 items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-brand-200 transition-all hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:shadow-none dark:focus-visible:ring-offset-surface-card-dark"
            >
              {t('wizard.source.goToAgents')}
            </Link>
          </div>
        ) : (
          <>
            <select
              id="backup-config-agent"
              value={data.agent_id}
              onChange={(e) => {
                onChange({ ...data, agent_id: e.target.value });
                setErrors({ ...errors, agent_id: undefined });
              }}
              className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
            >
              <option value="">{t('wizard.source.selectAnAgent')}</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.type}) -{' '}
                  {agent.status?.toUpperCase() === 'ONLINE'
                    ? t('wizard.source.online')
                    : t('wizard.source.offline')}
                </option>
              ))}
            </select>
            {data.agent_id && !selectedAgentIsOnline && (
              <p className="mt-2 text-xs font-medium text-state-warn-light dark:text-state-warn-dark">
                {t('wizard.source.agentOfflineWarning')}
              </p>
            )}
          </>
        )}
        {errors.agent_id && (
          <p className="mt-2 text-xs font-bold text-state-danger-light dark:text-state-danger-dark ml-1">
            {errors.agent_id}
          </p>
        )}
      </div>
    </div>
  );
}
